import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from '@/components/ui/dialog';
import {Button} from '@/components/ui/button';
import {Copy, CheckCheck, Code} from 'lucide-react';
import {useState} from 'react';
import {useToast} from '@/hooks/use-toast';

interface RewardInstallModalProps {
    isOpen: boolean;
    onClose: () => void;
    rewardId: number;
}

export const RewardInstallModal = ({isOpen, onClose, rewardId}: RewardInstallModalProps) => {
    const [copied, setCopied] = useState(false);
    const {toast} = useToast();

    const scriptCode = `<div datareward="${rewardId}"></div>`;

    const copyToClipboard = () => {
        navigator.clipboard.writeText(scriptCode);
        setCopied(true);
        toast({
            title: 'Código copiado!',
            description: 'O código foi copiado para a área de transferência.',
        });
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Code className="h-5 w-5"/>
                        Como Instalar o Offerwall
                    </DialogTitle>
                    <DialogDescription>
                        Siga os passos abaixo para adicionar este offerwall ao seu site
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                    {/* Código */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Código do Offerwall</label>
                        <div className="relative">
                            <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                {scriptCode}
                            </div>
                            <Button
                                type="button"
                                size="sm"
                                onClick={copyToClipboard}
                                className="absolute top-2 right-2"
                                variant="secondary"
                            >
                                {copied ? (
                                    <>
                                        <CheckCheck className="h-4 w-4 mr-2"/>
                                        Copiado!
                                    </>
                                ) : (
                                    <>
                                        <Copy className="h-4 w-4 mr-2"/>
                                        Copiar
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Instruções */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                            📋 Passo a passo para instalação
                        </h3>

                        <ol className="space-y-4 text-sm text-blue-900">
                            <li className="flex gap-3">
                                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 bg-blue-600 text-white rounded-full text-xs font-bold">
                                    1
                                </span>
                                <div>
                                    <p className="font-semibold mb-1">Copie o código acima</p>
                                    <p className="text-blue-800">Clique no botão "Copiar" para copiar o código para sua área de transferência.</p>
                                </div>
                            </li>

                            <li className="flex gap-3">
                                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 bg-blue-600 text-white rounded-full text-xs font-bold">
                                    2
                                </span>
                                <div>
                                    <p className="font-semibold mb-1">Abra o código HTML do seu site</p>
                                    <p className="text-blue-800">Acesse o arquivo HTML da página onde deseja exibir o reward.</p>
                                </div>
                            </li>

                            <li className="flex gap-3">
                                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 bg-blue-600 text-white rounded-full text-xs font-bold">
                                    3
                                </span>
                                <div>
                                    <p className="font-semibold mb-1">Cole o código dentro da tag &lt;body&gt;</p>
                                    <p className="text-blue-800">Adicione o código copiado dentro da tag <code className="bg-blue-100 px-1 py-0.5 rounded">&lt;body&gt;</code> do seu HTML, preferencialmente no final, antes do fechamento <code className="bg-blue-100 px-1 py-0.5 rounded">&lt;/body&gt;</code>.</p>
                                </div>
                            </li>

                            <li className="flex gap-3">
                                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 bg-blue-600 text-white rounded-full text-xs font-bold">
                                    4
                                </span>
                                <div>
                                    <p className="font-semibold mb-1">Salve e publique as alterações</p>
                                    <p className="text-blue-800">Salve o arquivo e atualize seu site. O reward será exibido automaticamente conforme as configurações definidas.</p>
                                </div>
                            </li>
                        </ol>
                    </div>

                    {/* Exemplo visual */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3 text-sm">💡 Exemplo de implementação</h4>
                        <div className="bg-gray-900 text-gray-300 p-4 rounded font-mono text-xs overflow-x-auto">
                            <pre>{`<!DOCTYPE html>
<html>
<head>
    <title>Meu Site</title>
</head>
<body>
    <!-- Seu conteúdo aqui -->
    
    <!-- Reward -->
    ${scriptCode}
</body>
</html>`}</pre>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t mt-6">
                    <Button variant="outline" onClick={onClose}>
                        Fechar
                    </Button>
                    <Button onClick={copyToClipboard} className="bg-blue-600 hover:bg-blue-700">
                        <Copy className="h-4 w-4 mr-2"/>
                        Copiar Código
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};